import React, { useState } from 'react';

interface OperatorPricingSectionProps {
  onGetStarted: (pkg: { minutes: number; price: number }) => void;
  initialMinutes?: number;
}

const OperatorPricingSection: React.FC<OperatorPricingSectionProps> = ({ onGetStarted, initialMinutes = 300 }) => {
  const [selectedMinutes, setSelectedMinutes] = useState(initialMinutes);
  const [customAmount, setCustomAmount] = useState(initialMinutes.toString());
  const [dailyCalls, setDailyCalls] = useState(5);
  const [avgDuration, setAvgDuration] = useState(3);
  const [showCalculator, setShowCalculator] = useState(false);
  const [pricingTiers, setPricingTiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [weeklyCalls, setWeeklyCalls] = useState<number | null>(null);

  // Load pricing from API on mount
  React.useEffect(() => {
    fetch('/api/pricing/tiers')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setPricingTiers(data.tiers);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load pricing:', err);
        setLoading(false);
      });
  }, []);

  // Get price per minute from API tiers
  const getPricePerMinute = (minutes: number): number => {
    if (pricingTiers.length === 0) return 0.58; // fallback

    const tier = pricingTiers.find(t =>
      t.min_minutes <= minutes && (t.max_minutes === null || t.max_minutes >= minutes)
    );

    return tier ? parseFloat(tier.price_per_minute) : 0.58;
  };

  const calculatePrice = (minutes: number) => {
    return Math.round(minutes * getPricePerMinute(minutes));
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setSelectedMinutes(value);
    setCustomAmount(value.toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value && !isNaN(parseInt(value))) {
      const minutes = Math.min(Math.max(parseInt(value), 10), 5000);
      setSelectedMinutes(minutes);
    }
  };

  const quickOptions = [
    { minutes: 50, label: 'Pradedantiesiems' },
    { minutes: 100, label: 'Standartinis' },
    { minutes: 250, label: 'Populiariausias' },
    { minutes: 500, label: 'Geriausias santykis' },
    { minutes: 1000, label: 'Verslui' }
  ];

  const callVolumeOptions = [
    { calls: 10, label: '10' },
    { calls: 20, label: '20' },
    { calls: 30, label: '30' },
    { calls: 40, label: '40' },
    { calls: 50, label: '50+' }
  ];

  const getRecommendedPlan = (weeklyCallsCount: number) => {
    // Assume 3 minutes per call average
    const monthlyMinutes = weeklyCallsCount * 3 * 4.3; // 4.3 weeks per month

    if (monthlyMinutes <= 75) return { plan: 'Pradedantiesiems', minutes: 50, calls: Math.round(monthlyMinutes / 3) };
    if (monthlyMinutes <= 175) return { plan: 'Standartinis', minutes: 100, calls: Math.round(monthlyMinutes / 3) };
    if (monthlyMinutes <= 375) return { plan: 'Populiariausias', minutes: 250, calls: Math.round(monthlyMinutes / 3) };
    if (monthlyMinutes <= 750) return { plan: 'Geriausias santykis', minutes: 500, calls: Math.round(monthlyMinutes / 3) };
    return { plan: 'Verslui', minutes: 1000, calls: Math.round(monthlyMinutes / 3) };
  };

  const handleCallVolumeSelect = (calls: number) => {
    setWeeklyCalls(calls);
    const recommended = getRecommendedPlan(calls);
    setSelectedMinutes(recommended.minutes);
    setCustomAmount(recommended.minutes.toString());
  };

  const currentPrice = calculatePrice(selectedMinutes);
  const pricePerMinute = getPricePerMinute(selectedMinutes);

  if (loading) {
    return (
      <div style={{
        padding: '3rem 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
      }}>
        <div style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', color: '#666' }}>Kraunama kainodaros informacija...</div>
      </div>
    );
  }

  return (
    <div id="kainos" style={{
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
      padding: 'clamp(2rem, 5vw, 4rem) 0',
      minHeight: '100vh',
      width: '100%',
      boxSizing: 'border-box',
      margin: '0',
      position: 'relative'
    }}>
      <style>{`
        /* Mobile responsive grid adjustments */
        @media (max-width: 768px) {
          .call-volume-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 0.5rem !important;
          }
          .call-volume-grid button {
            padding: 0.75rem 0.25rem !important;
            font-size: 1.1rem !important;
          }
          .quick-options-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.75rem !important;
          }
        }
        @media (max-width: 480px) {
          .call-volume-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
      <div className="container" style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 1rem',
        boxSizing: 'border-box'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(1.5rem, 4vw, 3rem)' }}>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 5vw, 3rem)',
            marginBottom: '1rem',
            fontFamily: 'Georgia, serif',
            color: '#000042',
            padding: '0 1rem'
          }}>
            Minutės Jūsų AI Asistentui
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
            color: '#666',
            padding: '0 1rem'
          }}>
            Kiekvienas užsakymas unikalus • Minutės galioja 12 mėnesių
          </p>
        </div>

        {/* Call Volume Estimator */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: 'clamp(1rem, 4vw, 2.5rem)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
          border: '2px solid #e0e0e0',
          boxSizing: 'border-box'
        }}>
          <h3 style={{
            textAlign: 'center',
            fontSize: '1.5rem',
            color: '#000042',
            marginBottom: '0.5rem',
            fontWeight: '600'
          }}>
            Kiek skambučių gaunate per savaitę?
          </h3>
          <p style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '0.95rem',
            marginBottom: '1.5rem'
          }}>
            Padėsime išsirinkti tinkamą planą pagal Jūsų poreikius
          </p>

          {/* Call Volume Buttons */}
          <div className="call-volume-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            {callVolumeOptions.map((option) => (
              <button
                key={option.calls}
                onClick={() => handleCallVolumeSelect(option.calls)}
                style={{
                  padding: '1rem 0.5rem',
                  border: weeklyCalls === option.calls ? '3px solid #000042' : '2px solid #e0e0e0',
                  borderRadius: '12px',
                  background: weeklyCalls === option.calls ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  color: weeklyCalls === option.calls ? '#000042' : '#333'
                }}
                onMouseOver={(e) => {
                  if (weeklyCalls !== option.calls) {
                    e.currentTarget.style.background = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#000042';
                  }
                }}
                onMouseOut={(e) => {
                  if (weeklyCalls !== option.calls) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Recommendation Display */}
          {weeklyCalls !== null && (
            <div style={{
              background: 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '2px solid #4a90e2'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{ fontSize: '2rem' }}>📊</div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    marginBottom: '0.25rem'
                  }}>
                    Rekomendacija
                  </div>
                  <div style={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    color: '#000042',
                    marginBottom: '0.25rem'
                  }}>
                    Jums tinka: {getRecommendedPlan(weeklyCalls).plan.toUpperCase()} planas ({getRecommendedPlan(weeklyCalls).minutes} min)
                  </div>
                  <div style={{
                    fontSize: '0.95rem',
                    color: '#555'
                  }}>
                    Tai apie {getRecommendedPlan(weeklyCalls).calls} skambučių per mėnesį (~3 min kiekvienas)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Counter Card */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '3rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
          border: '3px solid #000042'
        }}>
          {/* Counter Display */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              fontSize: '5rem',
              fontWeight: 'bold',
              color: '#000042',
              fontFamily: 'Georgia, serif',
              lineHeight: 1,
              marginBottom: '0.5rem'
            }}>
              {selectedMinutes}
            </div>
            <div style={{ fontSize: '1.5rem', color: '#666' }}>
              minučių
            </div>
          </div>

          {/* Price Display */}
          <div style={{
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '15px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#000042' }}>
              €{currentPrice}
            </div>
            <div style={{ fontSize: '1rem', color: '#000042', opacity: 0.8 }}>
              €{pricePerMinute.toFixed(2)} už minutę
            </div>
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.5)',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: '#000042'
            }}>
              Sutaupote: €{(selectedMinutes * 0.58 - currentPrice).toFixed(2)}
            </div>
          </div>

          {/* Slider */}
          <div style={{ marginBottom: '2rem' }}>
            <input
              type="range"
              min="10"
              max="2000"
              step="10"
              value={selectedMinutes}
              onChange={handleSliderChange}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '5px',
                background: `linear-gradient(to right, #FFD700 0%, #FFD700 ${(selectedMinutes / 2000) * 100}%, #e0e0e0 ${(selectedMinutes / 2000) * 100}%, #e0e0e0 100%)`,
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                cursor: 'pointer'
              }}
            />
            <style>{`
              input[type="range"]::-webkit-slider-thumb {
                appearance: none;
                width: 24px;
                height: 24px;
                background: #000042;
                border: 3px solid #FFD700;
                border-radius: 50%;
                cursor: pointer;
              }
              input[type="range"]::-moz-range-thumb {
                width: 24px;
                height: 24px;
                background: #000042;
                border: 3px solid #FFD700;
                border-radius: 50%;
                cursor: pointer;
                border: none;
              }
            `}</style>
          </div>

          {/* Custom Input */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <label style={{ color: '#666', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              Arba įveskite tikslų skaičių:
            </label>
            <input
              type="number"
              value={customAmount}
              onChange={handleInputChange}
              min="10"
              max="5000"
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1.2rem',
                border: '2px solid #000042',
                borderRadius: '8px',
                width: '200px',
                textAlign: 'center',
                fontWeight: 'bold',
                color: '#000042'
              }}
            />
          </div>

          {/* Minutes Calculator */}
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: showCalculator ? '1rem' : 0
            }}>
              <h4 style={{ margin: 0, color: '#000042' }}>
                Minučių skaičiuoklė
              </h4>
              <button
                onClick={() => setShowCalculator(!showCalculator)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#000042',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  padding: '0.5rem'
                }}
              >
                {showCalculator ? '▲' : '▼'}
              </button>
            </div>

            {showCalculator && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                      Skambučių per dieną
                    </label>
                    <input
                      type="number"
                      value={dailyCalls}
                      onChange={(e) => setDailyCalls(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                      Vid. trukmė (min)
                    </label>
                    <input
                      type="number"
                      value={avgDuration}
                      onChange={(e) => setAvgDuration(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    />
                  </div>
                </div>

                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <span style={{ color: '#000042', fontSize: '0.9rem' }}>Rekomenduojama per mėnesį:</span>
                    <strong style={{ color: '#000042', fontSize: '1.3rem', marginLeft: '0.5rem' }}>
                      {dailyCalls * avgDuration * 22} minučių
                    </strong>
                  </div>
                  <button
                    onClick={() => {
                      const recommendedMinutes = dailyCalls * avgDuration * 22;
                      setSelectedMinutes(recommendedMinutes);
                      setCustomAmount(recommendedMinutes.toString());
                    }}
                    style={{
                      padding: '0.5rem 1.5rem',
                      background: '#000042',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    Pasirinkti
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Quick Options */}
          <div className="quick-options-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {quickOptions.map((option) => (
              <button
                key={option.minutes}
                onClick={() => {
                  setSelectedMinutes(option.minutes);
                  setCustomAmount(option.minutes.toString());
                }}
                style={{
                  padding: '1rem',
                  border: selectedMinutes === option.minutes ? '2px solid #000042' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  background: selectedMinutes === option.minutes ? '#f8f9fa' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontWeight: 'bold', color: '#000042' }}>{option.minutes}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{option.label}</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#FFD700' }}>
                  €{calculatePrice(option.minutes)}
                </div>
              </button>
            ))}
          </div>

          {/* Purchase Button */}
          <button
            onClick={() => onGetStarted({ minutes: selectedMinutes, price: currentPrice })}
            style={{
              width: '100%',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #000042 0%, #000028 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
            }}
          >
            PIRKTI {selectedMinutes} MINUČIŲ UŽ €{currentPrice}
          </button>

          {/* Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginTop: '2rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#000042' }}>~{Math.round(selectedMinutes / 3)}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>Skambučių</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#000042' }}>{Math.round(selectedMinutes / 10)}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>Dienų (10min/d)</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#000042' }}>12 mėn</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>Galiojimas</div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          textAlign: 'center'
        }}>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></div>
            <div style={{ fontWeight: 'bold', color: '#000042' }}>24/7 Aptarnavimas</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Visada pasiekiamas</div>
          </div>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></div>
            <div style={{ fontWeight: 'bold', color: '#000042' }}>3 Kalbos</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>LT / EN / RU</div>
          </div>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></div>
            <div style={{ fontWeight: 'bold', color: '#000042' }}>Ataskaitos</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Po kiekvieno skambučio</div>
          </div>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></div>
            <div style={{ fontWeight: 'bold', color: '#000042' }}>Saugus mokėjimas</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Per Paysera</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorPricingSection;